import { Suspense, lazy, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import { useDispatch, useSelector } from 'store';
import { datLai, getDangKyThucTapHienTai, moDialog } from 'store/slices/dangKyThucTap';
import { duocKeKhai } from 'ui-component/capnhatthongtin/CapNhatThongTinGate';
import FeatureErrorBoundary from 'ui-component/FeatureErrorBoundary';

// Tách khỏi bundle khởi động: chỉ hạnh sinh Trung Minh mới cần tới.
const DangKyThucTapDialog = lazy(() => import('./DangKyThucTapDialog'));

// Bỏ dấu `/` thừa ở cuối trước khi so đường dẫn. Trang chủ là '/' nên sau khi bỏ thì thành
// chuỗi rỗng — đó là giá trị cần so.
const O_TRANG_CHU = (duongDan) => String(duongDan || '').replace(/\/+$/, '') === '';

// Toàn bộ logic tính năng nằm ở đây, và component này luôn được render BÊN TRONG
// ErrorBoundary. Boundary là con thì không bắt được lỗi do cha ném ra, nên mọi hook
// có thể ném (selector, effect) phải nằm dưới boundary một tầng.
const DangKyThucTapGateHanhSinh = ({ maHs }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const daMoLanNay = useRef(false);

    const { phieu, canEdit } = useSelector((state) => state.dangKyThucTap);

    // Phụ thuộc maHs, KHÔNG chốt theo `loaded`: đổi tài khoản trong cùng tab thì nạp lại đúng
    // dữ liệu người mới. MainLayout không remount khi chuyển route con nên đây vẫn là một
    // request mỗi phiên đăng nhập.
    useEffect(() => {
        // Xoá sạch dữ liệu người trước TRƯỚC khi tải dữ liệu người mới: không ai được nhìn
        // thấy phiếu của người khác dù chỉ một nhịp.
        dispatch(datLai());
        dispatch(getDangKyThucTapHienTai());
    }, [dispatch, maHs]);

    // Nhắc MỖI LẦN hạnh sinh về trang chủ cho tới khi chọn xong nơi thực tập — cố ý không ghi
    // nhớ "Để sau" ở trình duyệt. Chọn rồi thì `phieu.noiThucTap` có giá trị nên thôi nhắc.
    //
    // `daMoLanNay` được ĐẶT LẠI ngay khi rời trang chủ, nên lần sau quay về là nhắc tiếp.
    // Trong cùng một lần ở trang chủ thì nó chặn việc bật lại: bấm "Để sau" xong mà store
    // đổi cũng không dựng popup lên trước mũi người ta.
    useEffect(() => {
        if (!O_TRANG_CHU(location.pathname)) {
            daMoLanNay.current = false;
            return;
        }

        if (!phieu || !canEdit || phieu.noiThucTap) return;
        if (daMoLanNay.current) return;

        daMoLanNay.current = true;
        dispatch(moDialog());
    }, [dispatch, phieu, canEdit, location.pathname]);

    // Không thuộc danh sách thì Dialog chỉ return null — đừng tải chunk cho hàng trăm hạnh sinh.
    return phieu ? (
        <Suspense fallback={null}>
            <DangKyThucTapDialog />
        </Suspense>
    ) : null;
};

/**
 * Cổng đăng ký thực tập Trung Minh: tải một lần khi đăng nhập, tự bật popup ở trang chủ
 * cho hạnh sinh có trong danh sách mà chưa chọn nơi thực tập.
 */
const DangKyThucTapGate = () => {
    const { user } = useAuth();

    // Tài khoản không gắn mã hạnh sinh thì không hỏi: backend cũng trả phieu = null.
    if (!duocKeKhai(user)) return null;

    // Lỗi trong tính năng này KHÔNG được phép làm trắng màn hình toàn app.
    return (
        <FeatureErrorBoundary ten="đăng ký thực tập">
            <DangKyThucTapGateHanhSinh maHs={user.maHs} />
        </FeatureErrorBoundary>
    );
};

export default DangKyThucTapGate;
